import { useMemo } from 'react';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { formatCurrencyByLocale } from '@tink/core/src/utils';
import { maskAccountNumber } from '../../../../utilities/utils';
import { styled } from '../../../../core/theme/styled';
import { DirectPaymentDetailsModel } from '../../../models/payments';
import { useUrlQuery } from '../../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { Table } from '../../../../core/atoms/Table/Table';
import { messages } from './messages';

type DirectPaymentDetailsBannerProps = {
  paymentDetails: DirectPaymentDetailsModel;
  sourceAccount?: string;
};

export const StyledPaymentDetailsBanner = styled.div`
  background-color: ${(props) => props.theme.configuration.tintedBackgroundColor};
  padding: 0 ${(props) => props.theme.sizes.xsmall};
  width: 100%;
  margin-bottom: ${(props) => props.theme.sizes.normedium};
`;

export const DirectPaymentDetailsBanner: React.FC<DirectPaymentDetailsBannerProps> = ({
  paymentDetails,
  sourceAccount,
}) => {
  const intl = useIntl();
  const { locale } = useUrlQuery();

  const items = useMemo(() => {
    const { amount, currency, executionDate, recipientName } = paymentDetails;

    const totalPaymentItem = {
      value: formatCurrencyByLocale(amount, currency, locale),
      label: intl.formatMessage(messages.totalPayment),
    };

    const payingFromItem = sourceAccount
      ? [
          {
            value: maskAccountNumber(sourceAccount),
            label: intl.formatMessage(messages.payingFrom),
          },
        ]
      : [];

    const payingToItem = {
      value: recipientName,
      label: intl.formatMessage(messages.payingTo),
    };

    const executionDateItem = executionDate
      ? [
          {
            value: executionDate,
            label: intl.formatMessage(messages.executionDate),
          },
        ]
      : [];

    return {
      content: [...payingFromItem, payingToItem, ...executionDateItem],
      summary: totalPaymentItem,
    };
  }, [intl, locale, paymentDetails, sourceAccount]);

  return (
    <StyledPaymentDetailsBanner data-testid="paymentDetails">
      <Table content={items.content} summary={items.summary} />
    </StyledPaymentDetailsBanner>
  );
};
